import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Landmark, ChevronDown, Check, ArrowUpRight } from "lucide-react";
import { FadeUp, Overline } from "@/components/Reveal";
import { RiseWordmark, StarField } from "@/components/RiseWordmark";

const IMG = {
  riseLogo: "/assets/rise27-logo-space.webp",
  wallieLogo: "/assets/wallie-logo.jpg",
  cetateDrona: "/assets/cetate-drona-apus.jpg",
  cetateAerian: "/assets/cetate-aerian-sepia.webp",
  cetateZiduri: "/assets/cetate-ziduri-bastion.jpg",
};

const FILTERS = [
  { id: "toate", label: "Toate Proiectele", testid: "projects-filter-all" },
  { id: "viitor", label: "Proiecte Viitoare", testid: "projects-filter-upcoming" },
  { id: "finalizate", label: "Proiecte Finalizate", testid: "projects-filter-completed" },
];

const Tag = ({ children }) => (
  <span className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
    {children}
  </span>
);

const RiseCard = () => {
  const [open, setOpen] = useState(false);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="project-card-rise27"
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#060912]"
    >
      <StarField className="opacity-70" />
      <div className="relative grid grid-cols-1 lg:grid-cols-2">
        <div className="relative flex items-center justify-center p-4 sm:p-6">
          <img
            src={IMG.riseLogo}
            alt="Sigla festivalului RISE 27'"
            className="max-h-[520px] w-full rounded-2xl object-contain"
          />
          <span className="absolute left-6 top-6 rounded-full bg-neon px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-canvas">
            În Curând · 2027
          </span>
        </div>
        <div className="p-8 lg:p-12">
          <Overline>Proiect Principal · Alba Iulia</Overline>
          <h2 className="mt-5">
            <RiseWordmark className="text-4xl sm:text-5xl" testid="rise27-card-wordmark" />
          </h2>
          <p className="mt-6 text-base leading-relaxed text-neutral-300">
            Cel mai mare festival din Alba Iulia dedicat copiilor, adolescenților
            și tinerilor. Concerte live cu muzica pe care o ascultă tinerii —
            hip-hop, trap, pop, dance, rock și electronic — combinate cu istoria
            vie a orașului: un show spectaculos și o lecție de patrimoniu în
            același weekend.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Tag>Concerte</Tag>
            <Tag>Muzică Tânără</Tag>
            <Tag>Istorie</Tag>
            <Tag>Ateliere</Tag>
            <Tag>Familii</Tag>
          </div>

          <div data-testid="rise27-heritage-info" className="mt-8 rounded-2xl border-l-2 border-neon bg-carddark p-6">
            <p className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-neon">
              <Landmark className="h-4 w-4" /> Ce vei descoperi
            </p>
            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-neutral-300">
              <li><strong className="text-white">1 Decembrie 1918 · Marea Unire</strong> — cum s-a votat unirea în Sala Unirii, cine au fost cei 1.228 de delegați și de ce Alba Iulia a devenit „capitala” acelei zile.</li>
              <li><strong className="text-white">Cetatea Alba Carolina</strong> — fortăreața Vauban construită între 1715 și 1738: cele șapte bastioane, rolul șanțurilor de apărare și cum se ridica un oraș-fortăreață.</li>
              <li><strong className="text-white">Porțile și simbolurile</strong> — ce ascund sculpturile de pe Poarta a III-a și cum se citesc stemele, blazoanele și inscripțiile latine de pe ziduri.</li>
              <li><strong className="text-white">Catedralele orașului</strong> — Sfântul Mihail (sec. XIII) și Catedrala Încoronării (1922): arhitectură gotică vs. neobizantină și mormintele principilor Transilvaniei.</li>
              <li><strong className="text-white">Horea, Cloșca și Crișan</strong> — răscoala din 1784 și locul execuției, povestite fără manual, pe înțelesul adolescenților.</li>
              <li><strong className="text-white">Meșteșuguri și viață de garnizoană</strong> — cum trăiau soldații, meșterii și familiile din cetate: ateliere demonstrative și reconstituiri.</li>
              <li><strong className="text-white">Patrimoniu în viața de azi</strong> — cum se conservă un monument, ce meserii lucrează la el și cum poate un tânăr să se implice.</li>
            </ul>
          </div>

          <div data-testid="rise27-cetate-gallery" className="mt-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
              Cetatea Alba Carolina · Gazda festivalului
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                { src: IMG.cetateDrona, alt: "Cetatea Alba Carolina văzută din dronă la apus" },
                { src: IMG.cetateAerian, alt: "Poarta a III-a și Catedrala Încoronării, Alba Iulia" },
                { src: IMG.cetateZiduri, alt: "Zidurile și bastioanele Cetății Alba Carolina" },
              ].map((img) => (
                <div key={img.src} className="overflow-hidden rounded-xl border border-white/10">
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            data-testid="rise27-details-button"
            onClick={() => setOpen((v) => !v)}
            className="group mt-8 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-neon transition-colors hover:text-white"
          >
            {open ? "Ascunde Detaliile" : "Vezi Detalii Complete"}
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>

          <Link
            to="/rise27"
            data-testid="rise27-page-link"
            className="group mt-6 flex w-fit items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
          >
            Program, scene &amp; tur cetate
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-6 space-y-3 border-t border-white/10 pt-6">
                  <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-flame">
                    Ce te așteaptă la RISE 27'
                  </p>
                  {[
                    "Cel mai mare show al orașului — scene, lumini și concerte cu artiști ascultați de tineri",
                    "Trasee interactive prin Cetatea Alba Carolina cu ghizi și actori",
                    "Ateliere de creație urbană: dans, graffiti, producție muzicală",
                    "Zone dedicate copiilor și activități pentru toată familia",
                    "Proiecții și povești despre Alba Iulia și zonele din jur",
                    "Un magnet pentru turiști și comunitățile din împrejurimi",
                  ].map((item) => (
                    <p key={item} className="flex items-start gap-3 text-sm leading-relaxed text-neutral-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                      {item}
                    </p>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.article>
  );
};

const WallieCard = () => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 40 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    data-testid="project-card-wallie"
    className="overflow-hidden rounded-3xl border border-white/10 bg-surface"
  >
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="order-2 p-8 lg:order-1 lg:p-12">
        <Overline>Cluj-Napoca · 2018 — 2020</Overline>
        <h2 className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
          Wallie Forest Kidz
        </h2>
        <p className="mt-6 text-base leading-relaxed text-neutral-300">
          Prima noastră grădiniță în natură din Cluj-Napoca. Timp de doi ani,
          copiii au învățat prin explorare directă în pădure, joc liber și
          stimulare senzorială — fără pereți, fără bănci, doar natură și curiozitate.
        </p>
        <ul className="mt-8 space-y-3 text-sm text-neutral-300">
          {[
            "Educație outdoor imersivă, în fiecare zi",
            "Joc liber și explorare ghidată în pădure",
            "O comunitate de familii și educatori dedicați",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
          <MapPin className="h-3.5 w-3.5 text-flame" />
          Proiect finalizat
        </p>
      </div>
      <div className="order-1 lg:order-2">
        <div className="relative">
          <img
            src={IMG.wallieLogo}
            alt="Wallie Forest Kidz — Grow Wild & Free"
            className="h-72 w-full object-cover lg:h-full"
          />
          <span className="absolute left-4 top-4 rounded-full bg-flame px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-white">
            Finalizat · 2018–2020
          </span>
        </div>
      </div>
    </div>
  </motion.article>
);

const BushidoCard = () => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    data-testid="project-card-bushido"
    className="relative overflow-hidden rounded-3xl border border-neon/25 bg-surface"
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-10 -top-16 select-none font-display text-[14rem] font-black leading-none text-white/[0.04]"
    >
      武
    </span>
    <div className="relative p-8 lg:p-12">
      <Overline>Proiect-umbrelă · Școli &amp; comunitate</Overline>
      <h2 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
        Bushido vs. <span className="text-neon">Bullying</span>
      </h2>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-300">
        Metoda noastră de prevenire a bullying-ului și a discriminării între
        copii, inspirată din codul samurailor: respect, curaj, integritate,
        compasiune, disciplină și onoare. Este scopul principal din spatele
        tuturor evenimentelor noastre — festivalul și turneul de fotbal duc
        același mesaj în rândul tinerilor și în școli.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Tag>Anti-Bullying</Tag>
        <Tag>Anti-Discriminare</Tag>
        <Tag>Școli</Tag>
        <Tag>Ateliere</Tag>
        <Tag>Mentorat</Tag>
      </div>
      <Link
        to="/bushido-vs-bullying"
        data-testid="bushido-page-link"
        className="group mt-8 flex w-fit items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
      >
        Descoperă proiectul
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  </motion.article>
);

const CupaCard = () => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    data-testid="project-card-cupa"
    className="overflow-hidden rounded-3xl border border-white/10 bg-surface"
  >
    <div className="p-8 lg:p-12">
      <Overline>Eveniment caritabil · Alba Iulia</Overline>
      <h2 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
        Mini-campionat de <span className="text-neon">fotbal caritabil</span>
      </h2>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-300">
        Turneu de fotbal pentru comunitate, în toamna sau iarna 2026, cu echipe
        de 8+1 pe teren full-size și o taxă simbolică de înscriere. Fondurile
        susțin proiectele noastre pentru copii și tineri, iar pe teren se aplică
        aceleași valori: fair-play, respect, zero discriminare.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Tag>Caritabil</Tag>
        <Tag>Fotbal 8+1</Tag>
        <Tag>Alba Iulia</Tag>
        <Tag>Toamna / iarna 2026</Tag>
      </div>
      <p className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">
        <MapPin className="h-3.5 w-3.5 text-flame" /> Data exactă se anunță
      </p>
      <Link
        to="/cupa-caritabila"
        data-testid="cupa-page-link"
        className="group mt-8 flex w-fit items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white"
      >
        Detalii &amp; înscriere echipă
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  </motion.article>
);

export default function Proiecte() {
  const [filter, setFilter] = useState("toate");
  const showRise = filter !== "finalizate";
  const showWallie = filter !== "viitor";
  const showBushido = filter !== "finalizate";
  const showCupa = filter !== "finalizate";

  return (
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
      <FadeUp>
        <Overline>Arhiva & Viitorul</Overline>
        <h1
          data-testid="projects-page-title"
          className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          Proiecte care <span className="text-neon">lasă urme</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400 sm:text-lg">
          De la grădinița din pădure a Clujului la cel mai mare festival din
          Alba Iulia — fiecare proiect reconectează oamenii cu natura și istoria,
          promovând respectul și zero toleranță la bullying.
        </p>
      </FadeUp>

      <FadeUp delay={0.15} className="mt-12 flex flex-wrap gap-3">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={f.testid}
            onClick={() => setFilter(f.id)}
            className={`rounded-full px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
              filter === f.id
                ? "bg-neon text-canvas"
                : "border border-white/15 text-neutral-400 hover:border-neon hover:text-neon"
            }`}
          >
            {f.label}
          </button>
        ))}
      </FadeUp>

      <div className="mt-14 space-y-14">
        <AnimatePresence mode="popLayout">
          {showBushido && <BushidoCard key="bushido" />}
          {showRise && <RiseCard key="rise" />}
          {showCupa && <CupaCard key="cupa" />}
          {showWallie && <WallieCard key="wallie" />}
        </AnimatePresence>
      </div>
    </div>
  );
}
